// @ts-nocheck
import React, { useState, useEffect, useCallback } from 'react';
import {
  FlaskConical, Plus, Search, Eye, Edit2, Trash2, X,
  CheckCircle2, Clock, ChevronRight, ArrowUpDown,
} from 'lucide-react';
import api from '../utils/api';

const batchStatuses = ['All', 'Planning', 'Brewing', 'Fermenting', 'Conditioning', 'Packaging', 'Complete'];

const statusColors = {
  Planning: 'status-planning',
  Brewing: 'status-brewing',
  Fermenting: 'status-fermenting',
  Conditioning: 'status-conditioning',
  Packaging: 'status-packaging',
  Complete: 'status-complete',
};

const Production = () => {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editBatch, setEditBatch] = useState(null);

  const emptyForm = {
    name: '', style: '', status: 'Planning', batchSize: '', batchUnit: 'BBL',
    brewDate: '', expectedEnd: '', abv: '', ibu: '', og: '', fg: '',
    brewmaster: '', notes: '',
  };
  const [form, setForm] = useState(emptyForm);

  const fetchBatches = useCallback(async () => {
    try {
      const res = await api.get('/batches/');
      // Handle paginated response - extract items array
      setBatches(res.data.items || res.data);
    } catch (err) {
      console.error('Failed to fetch batches', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchBatches(); }, [fetchBatches]);

  const filtered = batches
    .filter((b) => statusFilter === 'All' || b.status === statusFilter)
    .filter((b) => b.name.toLowerCase().includes(search.toLowerCase()) || b.id.toLowerCase().includes(search.toLowerCase()));

  const openAdd = () => { setEditBatch(null); setForm(emptyForm); setShowForm(true); };
  const openEdit = (batch) => {
    setEditBatch(batch);
    setForm({
      name: batch.name, style: batch.style, status: batch.status,
      batchSize: batch.batchSize, batchUnit: batch.batchUnit,
      brewDate: batch.brewDate, expectedEnd: batch.expectedEnd,
      abv: batch.abv, ibu: batch.ibu, og: batch.og, fg: batch.fg,
      brewmaster: batch.brewmaster, notes: batch.notes,
    });
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const payload = {
      ...form, batchSize: Number(form.batchSize), abv: Number(form.abv || 0),
      ibu: Number(form.ibu || 0), og: Number(form.og || 0), fg: Number(form.fg || 0),
    };
    try {
      if (editBatch) {
        await api.put(`/batches/${editBatch.id}`, payload);
      } else {
        await api.post('/batches/', payload);
      }
      fetchBatches();
      setShowForm(false);
    } catch (err) {
      console.error('Save failed', err);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this batch?')) {
      try {
        await api.delete(`/batches/${id}`);
        fetchBatches();
        if (selectedBatch?.id === id) setSelectedBatch(null);
      } catch (err) {
        console.error('Delete failed', err);
      }
    }
  };

  const statusCounts = batchStatuses.reduce((acc, s) => {
    acc[s] = s === 'All' ? batches.length : batches.filter((b) => b.status === s).length;
    return acc;
  }, {});

  return (
    <div className="animate-fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Production</h1>
          <p className="text-sm text-gray-500 mt-1">{batches.length} batches • {batches.filter((b) => b.status !== 'Complete').length} active</p>
        </div>
        <button onClick={openAdd} className="flex items-center gap-2 px-4 py-2.5 bg-[#0891B2] text-white text-sm font-semibold rounded-lg hover:bg-[#0e7490] transition-colors">
          <Plus size={16} /> New Batch
        </button>
      </div>

      {/* Status pipeline - horizontal scroll on mobile */}
      <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0 mb-6">
        <div className="flex sm:grid sm:grid-cols-7 gap-2 min-w-max sm:min-w-0">
          {batchStatuses.map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`flex-shrink-0 w-28 sm:w-auto px-3 py-2.5 rounded-lg text-center transition-all ${
                statusFilter === s
                  ? 'bg-[#0891B2] text-white shadow-sm'
                  : 'bg-white border border-gray-200 text-gray-600 hover:border-[#0891B2] hover:text-[#0891B2]'
              }`}
            >
              <p className="text-lg font-bold">{statusCounts[s]}</p>
              <p className="text-[10px] font-medium uppercase tracking-wider truncate">{s}</p>
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Batch List - Hidden on mobile when detail is shown */}
        <div className={`${selectedBatch ? 'hidden lg:block lg:w-1/2' : 'w-full'} transition-all`}>
          <div className="bg-white rounded-xl border border-gray-200">
            <div className="p-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search batches..." className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#0891B2] focus:border-transparent outline-none" />
              </div>
            </div>

            <div className="divide-y divide-gray-100">
              {filtered.map((batch) => (
                <div
                  key={batch.id}
                  onClick={() => setSelectedBatch(batch)}
                  className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                    selectedBatch?.id === batch.id ? 'bg-cyan-50/50 border-l-2 border-l-[#0891B2]' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-gray-900 text-sm">{batch.name}</h4>
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${statusColors[batch.status]}`}>
                          {batch.status}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500">{batch.id} • {batch.style} • {batch.batchSize} {batch.batchUnit}</p>
                      <p className="text-xs text-gray-400 mt-1">Brew date: {batch.brewDate}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={(e) => { e.stopPropagation(); openEdit(batch); }} className="p-1.5 text-gray-400 hover:text-[#0891B2] hover:bg-cyan-50 rounded"><Edit2 size={14} /></button>
                      <button onClick={(e) => { e.stopPropagation(); handleDelete(batch.id); }} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"><Trash2 size={14} /></button>
                      <ChevronRight size={16} className="text-gray-300 ml-1" />
                    </div>
                  </div>
                </div>
              ))}
              {filtered.length === 0 && (
                <div className="p-8 text-center text-gray-400 text-sm">No batches found</div>
              )}
            </div>
          </div>
        </div>

        {/* Batch Detail Panel - Full screen on mobile, half screen on desktop */}
        {selectedBatch && (
          <div className="fixed lg:relative inset-0 lg:inset-auto lg:w-1/2 bg-white lg:rounded-xl lg:border border-gray-200 animate-fade-in z-50 lg:z-auto lg:flex lg:flex-col lg:max-h-[600px]">
            <div className="p-5 border-b border-gray-100 lg:flex-shrink-0 sticky top-0 bg-white z-10">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">{selectedBatch.name}</h3>
                  <p className="text-sm text-gray-500">{selectedBatch.id} • {selectedBatch.style}</p>
                </div>
                <button onClick={() => setSelectedBatch(null)} className="p-1.5 hover:bg-gray-100 rounded"><X size={18} className="text-gray-400" /></button>
              </div>
            </div>

            <div className="p-5 space-y-6 pb-20 lg:flex-1 lg:overflow-y-auto lg:pb-5">
              {/* Specs Grid */}
              <div className="grid grid-cols-4 gap-3">
                {[
                  { label: 'ABV', value: `${selectedBatch.abv}%` },
                  { label: 'IBU', value: selectedBatch.ibu },
                  { label: 'OG', value: selectedBatch.og },
                  { label: 'FG', value: selectedBatch.fg },
                ].map((s) => (
                  <div key={s.label} className="bg-gray-50 rounded-lg p-3 text-center">
                    <p className="text-xs text-gray-500 uppercase font-medium">{s.label}</p>
                    <p className="text-lg font-bold text-gray-900 mt-0.5">{s.value}</p>
                  </div>
                ))}
              </div>

              {/* Details */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div><span className="text-gray-500">Batch Size:</span> <span className="font-medium text-gray-900 ml-1">{selectedBatch.batchSize} {selectedBatch.batchUnit}</span></div>
                <div><span className="text-gray-500">Brewmaster:</span> <span className="font-medium text-gray-900 ml-1">{selectedBatch.brewmaster}</span></div>
                <div><span className="text-gray-500">Brew Date:</span> <span className="font-medium text-gray-900 ml-1">{selectedBatch.brewDate}</span></div>
                <div><span className="text-gray-500">Expected End:</span> <span className="font-medium text-gray-900 ml-1">{selectedBatch.expectedEnd}</span></div>
              </div>

              {/* Notes */}
              {selectedBatch.notes && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Notes</h4>
                  <p className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3">{selectedBatch.notes}</p>
                </div>
              )}

              {/* Ingredients */}
              {selectedBatch.ingredients?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Ingredients</h4>
                  <div className="space-y-2">
                    {selectedBatch.ingredients.map((ing, i) => (
                      <div key={i} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg text-sm">
                        <span className="text-gray-700">{ing.name}</span>
                        <span className="font-medium text-gray-900">{ing.amount} {ing.unit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Timeline */}
              {selectedBatch.timeline?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Timeline</h4>
                  <div className="relative pl-6">
                    <div className="absolute left-2 top-1 bottom-1 w-0.5 bg-gray-200" />
                    {selectedBatch.timeline.map((event, i) => (
                      <div key={i} className="relative mb-4 last:mb-0">
                        <div className={`absolute -left-4 w-4 h-4 rounded-full border-2 ${
                          event.completed ? 'bg-[#0891B2] border-[#0891B2]' : 'bg-white border-gray-300'
                        } flex items-center justify-center`}>
                          {event.completed && <CheckCircle2 size={10} className="text-white" />}
                        </div>
                        <div className="ml-4">
                          <p className={`text-sm font-medium ${event.completed ? 'text-gray-900' : 'text-gray-500'}`}>{event.event}</p>
                          <p className="text-xs text-gray-400">{event.date}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* New/Edit Batch Modal */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl mx-4 animate-fade-in" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">{editBatch ? 'Edit Batch' : 'New Batch'}</h3>
              <button onClick={() => setShowForm(false)} className="p-1 hover:bg-gray-100 rounded"><X size={20} className="text-gray-400" /></button>
            </div>
            <form onSubmit={handleSave} className="p-5 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="col-span-2"><label className="block text-sm font-medium text-gray-700 mb-1">Beer Name</label><input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#0891B2] focus:border-transparent outline-none" placeholder="Amber Waves IPA" required /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Style</label><input value={form.style} onChange={(e) => setForm({...form, style: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#0891B2] focus:border-transparent outline-none" placeholder="American IPA" required /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Status</label><select value={form.status} onChange={(e) => setForm({...form, status: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#0891B2] focus:border-transparent outline-none">
                  {batchStatuses.filter(s => s !== 'All').map(s => <option key={s}>{s}</option>)}
                </select></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Batch Size</label><input type="number" value={form.batchSize} onChange={(e) => setForm({...form, batchSize: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#0891B2] focus:border-transparent outline-none" required /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Unit</label><select value={form.batchUnit} onChange={(e) => setForm({...form, batchUnit: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#0891B2] focus:border-transparent outline-none"><option>BBL</option><option>Gallons</option><option>Liters</option></select></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Brew Date</label><input type="date" value={form.brewDate} onChange={(e) => setForm({...form, brewDate: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#0891B2] focus:border-transparent outline-none" required /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Expected End</label><input type="date" value={form.expectedEnd} onChange={(e) => setForm({...form, expectedEnd: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#0891B2] focus:border-transparent outline-none" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">ABV %</label><input type="number" step="0.1" value={form.abv} onChange={(e) => setForm({...form, abv: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#0891B2] focus:border-transparent outline-none" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">IBU</label><input type="number" value={form.ibu} onChange={(e) => setForm({...form, ibu: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#0891B2] focus:border-transparent outline-none" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">OG</label><input type="number" step="0.001" value={form.og} onChange={(e) => setForm({...form, og: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#0891B2] focus:border-transparent outline-none" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">FG</label><input type="number" step="0.001" value={form.fg} onChange={(e) => setForm({...form, fg: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#0891B2] focus:border-transparent outline-none" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Brewmaster</label><input value={form.brewmaster} onChange={(e) => setForm({...form, brewmaster: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#0891B2] focus:border-transparent outline-none" /></div>
                <div className="col-span-2"><label className="block text-sm font-medium text-gray-700 mb-1">Notes</label><textarea rows={2} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#0891B2] focus:border-transparent outline-none resize-none" /></div>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">Cancel</button>
                <button type="submit" className="px-4 py-2 text-sm font-semibold text-white bg-[#0891B2] rounded-lg hover:bg-[#0e7490] transition-colors">{editBatch ? 'Save Changes' : 'Create Batch'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Production;
